import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { ThirdPartyTypeEnum } from '@e-socna/data-layer';
import { buildPath, nameof } from '@e-socna/utils';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';

import {
  PATH_ORGANIZATION_DEFAULT,
  PATH_PERSON_DEFAULT,
} from '../../../app/constants/paths.constant';
import { formatOrganizationIdentifierValue } from '../../../utils/format-organization-identifier.util';
import type { ReferencesContext_Civility } from '../../../contexts/ReferencesContext';
import type { ReferencesContext_GenericStatus } from '../../../contexts/ReferencesContext';

import { UseCase } from '../enums/use-case.enum';
import type { ThirdPartiesCmp_Person } from '../fragments/person.fragment';
import type { ThirdPartiesCmp_Organization } from '../fragments/organization.fragment';
import type { ThirdPartiesCmp_ThirdParty } from '../fragments/third-party.fragment';

export const THIRD_PARTY_STATUS_ID = 'thirdPartyStatus';

interface ActionProps {
  readonly thirdParty: ThirdPartiesCmp_ThirdParty;
}

const Action = ({ thirdParty }: Readonly<ActionProps>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    const path = buildPath(
      thirdParty.kind === ThirdPartyTypeEnum.Person
        ? PATH_PERSON_DEFAULT
        : PATH_ORGANIZATION_DEFAULT,
      { id: thirdParty._id },
    );
    void navigate(path);
  }, [navigate, thirdParty._id, thirdParty.kind]);

  const actionLabel = 'Consulter le Tiers';

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

export const getThirdPartyColumns = (
  useCase: UseCase,
  civilities: readonly ReferencesContext_Civility[],
  genericStatuses: readonly ReferencesContext_GenericStatus[],
): readonly GridColDef<ThirdPartiesCmp_ThirdParty>[] => {
  const columns: readonly GridColDef<ThirdPartiesCmp_ThirdParty>[] = [
    ...(useCase === UseCase.Visualize
      ? [
          {
            field: 'actions',
            headerName: 'Actions',
            description: 'Actions',
            editable: false,
            sortable: false,
            type: 'actions',
            getActions: (
              params: Readonly<GridRowParams<ThirdPartiesCmp_ThirdParty>>,
            ) => [<Action key="consult" thirdParty={params.row} />],
          } as const,
        ]
      : EMPTY_ARRAY),
    {
      field: nameof<ThirdPartiesCmp_Person>('kind'),
      headerName: 'Type',
      width: 200,
      description: 'Type',
      editable: false,
      sortable: true,
      type: 'singleSelect',
      valueOptions: [
        {
          value: ThirdPartyTypeEnum.Person,
          label: 'Personne',
        },
        {
          value: ThirdPartyTypeEnum.Organization,
          label: 'Organisation',
        },
      ],
    },
    {
      field: 'name',
      headerName: 'Nom',
      width: 600,
      description: 'Nom',
      editable: false,
      sortable: true,
      type: 'string',
      valueGetter: (_: unknown, row: Readonly<ThirdPartiesCmp_ThirdParty>) => {
        if (row.kind === ThirdPartyTypeEnum.Person) {
          const typedRow = row as ThirdPartiesCmp_Person;

          const civility = civilities.find(
            (civility) => civility._id === typedRow.civilityId,
          );

          const civilityPrefix = civility ? `${civility.label} ` : EMPTY_STRING;

          return `${civilityPrefix}${typedRow.firstName ? `${typedRow.firstName} ` : EMPTY_STRING}${typedRow.lastName}`;
        }
        const typedRow = row as ThirdPartiesCmp_Organization;
        const organizationIdentifierValue = formatOrganizationIdentifierValue(
          typedRow.identifier,
        );
        const organizationIdentifierPrefix = organizationIdentifierValue
          ? `${organizationIdentifierValue} - `
          : EMPTY_STRING;
        return `${organizationIdentifierPrefix}${typedRow.name}${
          typedRow.tradingName ? ` - ${typedRow.tradingName}` : EMPTY_STRING
        }`;
      },
    },
    {
      field: THIRD_PARTY_STATUS_ID,
      headerName: 'Statut',
      width: 200,
      description: 'Statut',
      editable: false,
      sortable: true,
      hideable: true,
      type: 'singleSelect',
      valueGetter: (_: unknown, row: Readonly<ThirdPartiesCmp_ThirdParty>) =>
        genericStatuses.find(
          (genericStatus) => genericStatus._id === row.statusId,
        )?.code ?? 'Inconnu',
      valueOptions: genericStatuses.map((genericStatus) => ({
        value: genericStatus.code,
        label: genericStatus.label,
      })),
    },
  ] as const;

  return columns;
};
